import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';

/**
 * ReviewNotesModal - Simple modal for optional review focus notes
 *
 * Features:
 * - 3-line textarea with placeholder
 * - Enter to submit, Escape to cancel
 * - Auto-focus on open
 */
function ReviewNotesModal({ isOpen, onClose, onSubmit }) {
  const [notes, setNotes] = useState('');
  const textareaRef = useRef(null);

  // Auto-focus textarea when modal opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
      setNotes(''); // Clear notes when opening
    }
  }, [isOpen]);

  // Keyboard shortcuts: Enter to submit, Escape to cancel
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, notes, onClose]);

  const handleSubmit = () => {
    onSubmit(notes.trim() || null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Review Notes
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
            (Optional) Focus on:
          </label>
          <textarea
            ref={textareaRef}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g., Verify calculations, and in particular check calculation [A]. Check chart X and Y for possible inconsistency."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       placeholder-gray-400 dark:placeholder-gray-500
                       focus:ring-2 focus:ring-purple-500 focus:border-transparent
                       resize-none"
          />
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Enter to submit · Esc to cancel
          </p>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300
                       hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600
                       hover:bg-purple-700 rounded-md transition-colors"
          >
            Run Review
          </button>
        </div>
      </div>
    </div>
  );
}

export default ReviewNotesModal;
